import { SAMPLE, C, num } from '../data/sample';
import type { Niche } from '../data/sample';
import { state$, openApp } from '../state';

const SHARE_PALETTE = [
  '#303030',
  '#9aa0a6',
  '#c2c6cb',
  '#8a8a8a',
  '#9aa0a6',
  '#aeb4ba',
  '#c2c6cb',
  '#d2d5d9',
  '#dee0e3',
  '#e8eaec',
];

interface SatRow {
  label: string;
  value: string;
  pct: string;
  bar: string;
  valColor: string;
  desc: string;
}

export function NicheOverview({ niche }: { niche: Niche; oppCount: number }) {
  const d = SAMPLE;
  const total = d.apps.reduce((a, b) => a + b.reviews, 0);
  const inc = d.apps[0]!;

  const satBreakdown: SatRow[] = [
    {
      label: 'App count',
      value: `${niche.appsReal} apps`,
      pct: '78%',
      bar: '#6d7175',
      valColor: C.ink2,
      desc: 'A large market, but crowded doesn’t mean closed — look at concentration.',
    },
    {
      label: 'Concentration',
      value: 'Low · HHI 0.21',
      pct: '32%',
      bar: C.openBar,
      valColor: C.open,
      desc: 'The leader holds only ~25% of reviews and 10 apps split it evenly → a fragmented niche with room to squeeze in.',
    },
    {
      label: 'Average rating',
      value: `★ ${niche.rating.toFixed(2)}`,
      pct: '95%',
      bar: C.satBar,
      valColor: C.sat,
      desc: 'Very high ratings = users are fairly happy with the basics → hard to unseat on core functions.',
    },
    {
      label: 'Momentum (review velocity)',
      value: '+8%/qtr',
      pct: '58%',
      bar: C.contestedBar,
      valColor: C.contested,
      desc: 'Still growing steadily; the niche is alive and creating new demand in emerging functions.',
    },
  ];

  const shareBars = d.apps.map((a, i) => ({
    pct: ((a.reviews / total) * 100).toFixed(1) + '%',
    color: SHARE_PALETTE[i]!,
    title: `${a.name} · ${num(a.reviews)} review`,
    id: a.id,
  }));
  const shareLegend = d.apps.slice(0, 5).map((a, i) => ({
    name: a.name,
    color: SHARE_PALETTE[i]!,
    pct: ((a.reviews / total) * 100).toFixed(0) + '%',
  }));

  const concentrationNote =
    '10 apps split the market and the leader holds only ~25% of reviews (low HHI) → it looks open. But the core functions are locked down by Loox/Judge.me; the opening is in new functions, not the Nth review app.';

  return (
    <div
      style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 18, alignItems: 'start' }}
    >
      <div
        style={{ background: '#fff', border: '1px solid #e1e3e5', borderRadius: 16, padding: 22 }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 4,
          }}
        >
          <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>
            Why the saturation score = {niche.sat}
          </h2>
          <span style={{ fontSize: 11, color: '#a5abb5', fontFamily: "'JetBrains Mono'" }}>
            updated 2 days ago
          </span>
        </div>
        <p style={{ margin: '4px 0 20px', color: '#616161', fontSize: 13.5 }}>
          Saturation = app count × <b>concentration</b> × avg rating × momentum. Few apps isn’t
          proof it’s open; many isn’t proof it’s closed.
        </p>
        {satBreakdown.map((b) => (
          <div key={b.label} style={{ marginBottom: 17 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                marginBottom: 5,
              }}
            >
              <span style={{ fontSize: 13.5, fontWeight: 700 }}>{b.label}</span>
              <span
                style={{
                  fontFamily: "'JetBrains Mono'",
                  fontSize: 13,
                  fontWeight: 600,
                  color: b.valColor,
                }}
              >
                {b.value}
              </span>
            </div>
            <div
              style={{
                height: 7,
                borderRadius: 5,
                background: '#ebebeb',
                overflow: 'hidden',
                marginBottom: 5,
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: b.pct,
                  borderRadius: 5,
                  background: b.bar,
                  transformOrigin: 'left',
                  animation: 'nl-grow .5s ease',
                }}
              />
            </div>
            <p style={{ margin: 0, fontSize: 12, color: '#6d7175', lineHeight: 1.45 }}>{b.desc}</p>
          </div>
        ))}

        {/* concentration market share */}
        <div style={{ marginTop: 22, paddingTop: 20, borderTop: '1px dashed #e1e3e5' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 10,
            }}
          >
            <span style={{ fontSize: 13.5, fontWeight: 700 }}>Market share by reviews (proxy)</span>
            <span style={{ fontSize: 12, fontFamily: "'JetBrains Mono'", color: '#6d7175' }}>
              HHI 0.21
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              height: 30,
              borderRadius: 8,
              overflow: 'hidden',
              border: '1px solid #e1e3e5',
            }}
          >
            {shareBars.map((s) => (
              <div
                key={s.id}
                onClick={() => openApp(s.id)}
                style={{
                  width: s.pct,
                  background: s.color,
                  cursor: 'pointer',
                  position: 'relative',
                  borderRight: '1px solid rgba(255,255,255,0.5)',
                }}
                title={s.title}
              />
            ))}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 11 }}>
            {shareLegend.map((l) => (
              <div
                key={l.name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  fontSize: 11.5,
                  color: '#616161',
                }}
              >
                <span style={{ width: 9, height: 9, borderRadius: 3, background: l.color }} />
                {l.name}{' '}
                <span style={{ fontFamily: "'JetBrains Mono'", color: '#a5abb5' }}>{l.pct}</span>
              </div>
            ))}
          </div>
          <p style={{ margin: '11px 0 0', fontSize: 12, color: '#6d7175', lineHeight: 1.5 }}>
            {concentrationNote}
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {/* incumbent spotlight */}
        <div
          style={{ background: '#fff', border: '1px solid #e1e3e5', borderRadius: 16, padding: 22 }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: '#6d7175',
              marginBottom: 14,
            }}
          >
            Niche incumbent
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div
              style={{
                width: 46,
                height: 46,
                borderRadius: 11,
                background: inc.iconBg,
                border: '1px solid #e1e3e5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 800,
                fontSize: 18,
                color: '#303030',
              }}
            >
              {inc.initials}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 17, color: '#303030' }}>{inc.name}</div>
              <div style={{ fontSize: 12.5, color: '#8a8a8a', fontFamily: "'JetBrains Mono'" }}>
                {num(inc.reviews)} reviews · ★ {inc.rating}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
            <span
              style={{
                fontFamily: "'JetBrains Mono'",
                fontSize: 34,
                fontWeight: 700,
                color: '#303030',
              }}
            >
              {((inc.reviews / total) * 100).toFixed(0)}%
            </span>
            <span style={{ fontSize: 13, color: '#8a8a8a' }}>est. review share</span>
          </div>
          <p style={{ margin: '10px 0 0', fontSize: 13, color: '#616161', lineHeight: 1.55 }}>
            Judge.me wins with a generous free plan + very strong core coverage. Hard to beat on
            basic reviews — but weak on Q&A, AI, and analytics.
          </p>
          <button
            onClick={() => openApp(inc.id)}
            style={{
              marginTop: 16,
              width: '100%',
              height: 38,
              background: '#fafbfb',
              border: '1px solid #e1e3e5',
              borderRadius: 9,
              color: '#303030',
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            View {inc.name} profile →
          </button>
        </div>

        {/* CTA cards */}
        <div
          onClick={() => state$.tab.set('matrix')}
          style={{
            background: '#fff',
            border: '1px solid #e1e3e5',
            borderRadius: 14,
            padding: 18,
            cursor: 'pointer',
            transition: '.18s',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 3 }}>
                Function Matrix →
              </div>
              <div style={{ fontSize: 12.5, color: '#6d7175' }}>
                Who does what · {d.functions.length} functions ×{' '}
                {niche.appsAnalyzed ?? d.apps.length} apps
              </div>
            </div>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: '#eef0f2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
              }}
            >
              ▦
            </div>
          </div>
        </div>
        <div
          onClick={() => state$.tab.set('opportunities')}
          style={{
            background: '#fff',
            border: '1px solid #e1e3e5',
            borderRadius: 14,
            padding: 18,
            cursor: 'pointer',
            transition: '.18s',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 3 }}>Opportunities →</div>
              <div style={{ fontSize: 12.5, color: '#6d7175' }}>
                {d.functions.filter((f) => f.opp.score >= 65).length} functions half-built & worth
                doing
              </div>
            </div>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: '#fff1e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
              }}
            >
              ◓
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
